import { ERequestSubmitted } from 'legacy/lut/classes/models/onboarding/IRequest'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import React, { useCallback, useMemo, useState } from 'react'

import { Button, IButtonProps } from 'core-components/library/button'
import { IInputProps } from 'core-components/library/inputs'
import { Loader, LoaderOverlay } from 'core-components/library/loader'
import { Controller, ValidationStatus } from 'core-components/library/validation'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { OnboardingInput } from 'onboarding/components/ui/theme'

import { BusinessInformationNa } from './partnerShip/BusinessInformationNa'
import { BusinessOwnerNa } from './partnerShip/BusinessOwnerNa'
import { CompanySales } from './partnerShip/CompanySales'
import {
  getPartnerShipNaDropdownsSources,
  PARTNERSHIP_NA_STATIC_DROPDOWNS_SOURCES,
} from './partnerShip/configs/staticDropdownsSources'
import { DiversityNa } from './partnerShip/DiversityNa'
import { usePartnerShipNA } from './partnerShip/hooks/usePartnerShipNA'
import { usePartnerShipNADistributors } from './partnerShip/hooks/usePartnerShipNADistributors'
import { OnlineAcceptanceNa } from './partnerShip/OnlineAcceptanceNa'
import { OtherDetailsNa } from './partnerShip/OtherDetailsNa'
import { QualifiedProgram } from './partnerShip/qualifiedProgram/QualifiedProgram'
import { SellingMethodsNa } from './partnerShip/SellingMethodsNa'
import { SellingMotionNa } from './partnerShip/SellingMotionNa'
import { SignatoryDetail } from './partnerShip/SignatoryDetail'
import { IBusinessOwner, IPartnerShipNA } from './partnerShip/types'

import styles from './PartnerShipNA.module.scss'

interface IProps {
  requestId: string
  audienceSettings: IAudienceCondition
  disabled?: boolean
}

export const PartnerShipNA = ({ requestId, audienceSettings, disabled }: IProps) => {
  const [item, setItem] = useState<IPartnerShipNA>({} as IPartnerShipNA)
  const [canSave, setCanSave] = useState(false)
  const [businessOwners, setBusinessOwners] = useState<IBusinessOwner[]>([{} as IBusinessOwner])

  const setFetchedItem = useCallback((value: IPartnerShipNA) => {
    setItem(value)
    if (value.BusinessOwner) {
      try {
        setBusinessOwners(JSON.parse(value.BusinessOwner))
      } catch (e) {
        // do nothing
      }
    }
  }, [])
  const { isLoading, isSaving, onSave, request, isRequestLoading } = usePartnerShipNA(
    setFetchedItem,
    requestId
  )

  const { crud: partnerShipNADistributorsCrud } = usePartnerShipNADistributors({
    country: request.Country,
  })

  const businessInformationCruds = useMemo(
    () => getPartnerShipNaDropdownsSources(request),
    [request]
  )

  const diversityNaCruds = useMemo(() => {
    return {
      ...PARTNERSHIP_NA_STATIC_DROPDOWNS_SOURCES,
      Distributors: partnerShipNADistributorsCrud,
    }
  }, [partnerShipNADistributorsCrud])

  const onChange = useCallback(
    (field: string, isNumber?: boolean) => (value: string | number) => {
      if (isNumber) {
        const num = Number(value)
        if (!Number.isNaN(num)) {
          setItem((prevState) => ({ ...prevState, [field]: num }))
        }
      } else {
        setItem((prevState) => ({ ...prevState, [field]: value }))
      }
    },
    []
  )

  return isLoading || isRequestLoading ? (
    <Loader />
  ) : (
    <Controller
      onValidationStatusChange={({ status }) => {
        if (status !== ValidationStatus.WIP) {
          setCanSave(status === ValidationStatus.Valid)
        }
      }}
    >
      <div className={styles.root__container}>
        <LoaderOverlay show={isSaving} />
        <div className={styles.root__type}>
          <div className={styles.text__type}>Partnership Type</div>
          <VisualAudienceExtended<IInputProps>
            component={OnboardingInput}
            write={audienceSettings}
            label={'Partnership Type'}
            value={item.PartnershipType || 'Reseller'}
            disabled
          />
        </div>
        <div className={styles.root}>
          <div className={styles.column}>
            <BusinessInformationNa
              onChange={onChange}
              writeAudience={audienceSettings}
              crud={businessInformationCruds}
              item={item}
              disabled={disabled}
            />

            <SellingMethodsNa
              onChange={(field: string, number?: boolean) => onChange(field, number)}
              writeAudience={audienceSettings}
              item={item}
              disabled={disabled}
            />
          </div>

          <div className={styles.column}>
            <SellingMotionNa
              onChange={(field: string, number?: boolean) => onChange(field, number)}
              writeAudience={audienceSettings}
              item={item}
              disabled={disabled}
            />

            <DiversityNa
              onChange={(field: string) => onChange(field)}
              writeAudience={audienceSettings}
              crud={diversityNaCruds}
              item={item}
              disabled={disabled}
            />
          </div>
          <div className={styles.column}>
            <OnlineAcceptanceNa
              onChange={(field: string) => onChange(field)}
              writeAudience={audienceSettings}
              crud={PARTNERSHIP_NA_STATIC_DROPDOWNS_SOURCES}
              item={item}
              isManuallySubmitted={
                request.Type === RequestTypes.Contract &&
                request.Submitted === ERequestSubmitted.manual
              }
              disabled={disabled}
            />

            <SignatoryDetail
              onChange={(field: string) => onChange(field)}
              writeAudience={audienceSettings}
              item={item}
              disabled={disabled}
            />
          </div>
        </div>

        <div className={styles.sectionWrapper}>
          <BusinessOwnerNa
            onChange={(value: IBusinessOwner[]) => setBusinessOwners([...value])}
            writeAudience={audienceSettings}
            businessOwners={businessOwners}
            disabled={disabled}
          />
        </div>

        <div className={styles.sectionWrapper}>
          <CompanySales
            item={item}
            writeAudience={audienceSettings}
            onChange={onChange}
            cruds={PARTNERSHIP_NA_STATIC_DROPDOWNS_SOURCES}
            disabled={disabled}
          />
        </div>

        <div className={styles.sectionWrapper}>
          <QualifiedProgram
            item={item}
            writeAudience={audienceSettings}
            onChange={onChange}
            disabled
          />
        </div>

        <OtherDetailsNa
          onChange={(field: string) => onChange(field)}
          writeAudience={audienceSettings}
          crud={PARTNERSHIP_NA_STATIC_DROPDOWNS_SOURCES}
          item={item}
          disabled={disabled}
        />
        <div className={styles.btnWrapper}>
          <VisualAudienceExtended<IButtonProps>
            disabled={disabled || !canSave}
            write={audienceSettings}
            component={Button}
            onClick={() => onSave({ ...item, BusinessOwner: JSON.stringify(businessOwners) })}
          >
            Save
          </VisualAudienceExtended>
        </div>
      </div>
    </Controller>
  )
}
